import { omit } from "lodash-es";

import StreamFilters, {
  type StreamFiltersFromOptionsInput,
  type StreamFiltersTag,
  type StreamRequestFilters,
} from "shared/helpers/StreamFilters";
import useStreamsStore from "shared/stores/streams";
import type { Maybe, Nullable, Nullish, Stream } from "shared/types";

type UninstantiatedStreamFilters = Partial<StreamFilters>;

export type AdvancedFiltersValue = Nullish<
  StreamFilters | StreamRequestFilters | UninstantiatedStreamFilters
>;

export function isStreamFiltersInstance(
  filters: NonNullable<AdvancedFiltersValue>
): filters is StreamFilters {
  return filters instanceof StreamFilters;
}

function isRequestFilters(
  filters: NonNullable<AdvancedFiltersValue>
): filters is StreamRequestFilters {
  return Object.keys(filters).some((key) => key.includes("_"));
}

function resolveTag(id: number): Maybe<StreamFiltersTag> {
  const streamsStore = useStreamsStore();

  const stream = [
    ...streamsStore.mediaBookmarkStreams,
    ...streamsStore.socialBookmarkStreams,
  ].find((bookmarkStream) => bookmarkStream.id === id);

  if (!stream) return undefined;

  return {
    color: stream.color,
    id: stream.id,
    label: stream.label,
  };
}

export function serializeAdvancedFilters(
  filters: AdvancedFiltersValue
): Nullable<StreamRequestFilters> {
  if (!filters) {
    return null;
  }

  if (isStreamFiltersInstance(filters)) {
    return filters.requestFilters();
  }

  if (isRequestFilters(filters)) {
    return filters;
  }

  // camelCase StreamFilters data — convert to StreamRequestFilters for consistent storage
  return toStreamFilters(filters, null).requestFilters();
}

export function toStreamFilters(
  filters: AdvancedFiltersValue,
  stream: Nullable<Stream>,
  initialFilters: Partial<StreamFilters> = {}
): StreamFilters {
  if (!filters) {
    return new StreamFilters(stream, initialFilters);
  }

  if (isStreamFiltersInstance(filters)) {
    const cloned = filters.clone();

    cloned.stream = stream;

    Object.assign(cloned, initialFilters);

    return cloned;
  }

  if (isRequestFilters(filters)) {
    const result = StreamFilters.fromOptions(
      filters as StreamFiltersFromOptionsInput,
      stream,
      {},
      resolveTag
    );

    Object.assign(result, initialFilters);

    return result;
  }

  return new StreamFilters(stream, {
    ...omit(filters, ["initialFilters", "stream"]),
    ...initialFilters,
  });
}
